import { cookie } from 'client/utils/cookie/cookie';
import {
  FLAVOR_GROUPS,
  FLAVOR_GROUP_NAMES,
  SESSION_INVENTORY_FILTER_MAX_LENGTHS,
  SESSION_FLAVOR_FILTERS,
} from 'client/site-modules/shared/constants/flavors';

export const SESSION_INVENTORY_FILTER_COOKIE_NAMES = {
  dtg: 'dtg',
  drv: 'drv',
  reach: 'reach',
};

const sessionFlavorPattern = FLAVOR_GROUPS[FLAVOR_GROUP_NAMES.SESSION];
const sessionFilterNames = Object.values(SESSION_FLAVOR_FILTERS);
const DEFAULT_SESSION_INVENTORY_FILTER_MAX_LENGTH = 15;

function normalizeFilterValue(filter, value) {
  if (typeof value !== 'string') return undefined;

  const normalizedValue = value.trim();
  const maxLength = SESSION_INVENTORY_FILTER_MAX_LENGTHS[filter] ?? DEFAULT_SESSION_INVENTORY_FILTER_MAX_LENGTH;

  return normalizedValue ? normalizedValue.slice(0, maxLength) : undefined;
}

/**
 * Returns filter names authorized by ses flavors.
 * @param {Object} flavorData
 * @returns {string[]}
 */
export function getSessionFlavorFilters(flavorData) {
  const sessionFilters = Object.keys(flavorData || {})
    .filter(token => sessionFlavorPattern.test(token))
    .flatMap(token => token.slice('ses:'.length).split(','))
    .filter(filter => sessionFilterNames.includes(filter));

  return [...new Set(sessionFilters)];
}

/**
 * Returns valid persisted inventory filter values from a cookie object.
 * @param {Object} cookies
 * @returns {Object}
 */
export function getSessionInventoryFiltersFromCookies(cookies = {}) {
  return Object.fromEntries(
    Object.entries(SESSION_INVENTORY_FILTER_COOKIE_NAMES)
      .map(([filter, cookieName]) => [filter, normalizeFilterValue(filter, cookies[cookieName])])
      .filter(([, value]) => value !== undefined)
  );
}

/**
 * Returns persisted inventory filters from the current browser/request cookies.
 * @returns {Object}
 */
export function getSessionInventoryFilters() {
  return getSessionInventoryFiltersFromCookies(cookie.get() || {});
}

/**
 * Returns URL filter values that are allowed to update the session cookie.
 * @param {Object} query
 * @param {Object} flavorData
 * @returns {Object}
 */
export function getSessionInventoryFilterUpdates({ query = {}, flavorData } = {}) {
  const allowedFilters = getSessionFlavorFilters(flavorData);

  return Object.fromEntries(
    allowedFilters
      .map(filter => [filter, normalizeFilterValue(filter, query[filter])])
      .filter(([, value]) => value !== undefined)
  );
}

/**
 * Returns merged persisted and URL-updated inventory filters from a request.
 * @param {Object} req
 * @param {Object} flavorData
 * @returns {Object}
 */
export function getInventoryFiltersFromRequest({ req, flavorData }) {
  const persistedInventoryFilters = getSessionInventoryFiltersFromCookies(req.cookies);
  const sessionInventoryFilterUpdates = getSessionInventoryFilterUpdates({
    query: req.query,
    flavorData: flavorData?.parsed,
  });
  return { ...persistedInventoryFilters, ...sessionInventoryFilterUpdates };
}
